import {ApiError, ApiResponse, useApiMutation, useApiQuery} from "../../../api/useApi";
import {MutationOptions, useQueryClient, UseQueryOptions} from "react-query";
import {Language} from "../../../api/useLanguage";


// #################################################
//                      TYPES
// #################################################
/**
 * Facility type
 * @typedef {Object} Facility
 */
export type Facility = {
    id: number;
    facilities?: Facility[]|null;
    group_id?: number | null;
    is_group?: boolean;
    language_id?: number | null;
    language?: Language;
    default_language?: Language;
    languages?: Language[];
    name?: string;
    address?: string;
    city?: string;
    zip_code?: string;
    country_id?: number | null;
    email?: string;
    phone?: string;
    website?: string;
    pipeline_stage_id?: number | null;
    pipeline_stage_name?: string | null;
    pipeline_stage_color?: string | null;
    special_status?: string | null;
    plug_id?: number | null;
    latitude?: number | null;
    longitude?: number | null;
    active_loyalty_program_id?: number | null;
    use_loyalty_levels?: boolean | null;
    has_active_review_connections?: boolean;
    review_alert_emails?: string;
    use_review_alert?: boolean;
    review_alert_threshold?: number;
    review_alert_recap_frequency?: string;
    room_count?: number | null;
    staff_count?: number | null;
    star_rating?: string | null;
    created_at?: string;
    updated_at?: string;
    deleted_at?: string;
    [key: string]: any;
}

/**
 * Type étendu avec les établissements enfants (pour les groupes)
 */
export type FacilityWithChildren = Facility & {
    child_facilities?: Facility[];
}

/**
 * Type pour les facilities avec coordonnées GPS (pour la carte)
 */
export type FacilityWithCoordinates = {
    id: number;
    name: string;
    email: string;
    address: string;
    city: string;
    zip_code: string;
    latitude: number;
    longitude: number;
    is_group: boolean;
    pipeline_stage_id: number | null;
    special_status: string | null;
    pipeline_stage?: {
        id: number;
        name: string;
        slug: string;
        color: string;
    };
    groups?: {
        id: number;
        name: string;
        /** « GLH › Teritoria » pour un sous-groupe, sinon le nom. */
        path?: string;
    }[];
    /** Onglet Groupes : le groupe parent d'un sous-groupe (retours V1, 9.6.1). */
    parent_group?: { id: number; name: string } | null;
    /** Liste des établissements : une campagne `active` existe (retours V1, 9.6.2). */
    has_campaign_in_progress?: boolean;
    campaigns_count?: number;
}

// #################################################
//                      QUERIES
// #################################################

/**
 * Hook to get all facilities of current user
 * @returns Facility[]
 */
export function useFacilities(opts?: { fromContext?: string }) {
    const params = new URLSearchParams();
    if (opts?.fromContext) params.set("from", opts.fromContext);
    const qs = params.toString();
    const url = `/facilities${qs ? `?${qs}` : ""}`;
    return useApiQuery<Facility[]>(url);
}
/**
 * Hook to get all facilities with pagination
 * @param {number} page - Current page number
 * @param isGroup
 * @param searchTerm
 * @returns {<ApiResponse<Facility[]>, ApiError>}
 */
export function useAllFacilities(
    page: number = 1,
    isGroup: boolean = false,
    searchTerm: string = "",
    filters: { groupId?: number | null; campaignStatus?: string | null } = {}
) {
    const pageSize = 10;
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        is_group: isGroup ? "1" : "0",
    });
    if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
    }
    if (filters.groupId) {
        params.append("group_id", String(filters.groupId));
    }
    if (filters.campaignStatus) {
        params.append("campaign_status", filters.campaignStatus);
    }
    const url = `/facilities-all?${params.toString()}`;
    return useApiQuery<ApiResponse<Facility[]>>(url);
}

/**
 * Les options d'un filtre par groupe, sur deux niveaux : chaque groupe suivi
 * de ses sous-groupes (« GLH », puis « GLH › Teritoria »). Choisir GLH prend
 * aussi les établissements de Teritoria (retours V1, 9.6.1).
 */
export function groupFilterOptions(groups: Facility[]): { value: string; label: string }[] {
    const byName = (a: Facility, b: Facility) => (a.name ?? '').localeCompare(b.name ?? '', 'fr');
    const ids = new Set(groups.map(g => g.id));

    // Une passe : les racines d'un côté, les sous-groupes rangés par parent de l'autre.
    const roots: Facility[] = [];
    const childrenOf = new Map<number, Facility[]>();
    for (const g of groups) {
        const parentId = g.parent_group?.id;
        if (parentId === undefined || !ids.has(parentId)) {
            roots.push(g);
        } else {
            const siblings = childrenOf.get(parentId);
            if (siblings) siblings.push(g);
            else childrenOf.set(parentId, [g]);
        }
    }

    return roots
        .sort(byName)
        .flatMap(parent => [
            { value: String(parent.id), label: parent.name ?? `#${parent.id}` },
            ...(childrenOf.get(parent.id) ?? []).sort(byName).map(child => ({
                value: String(child.id),
                label: `${parent.name} › ${child.name}`,
            })),
        ]);
}

/** Tous les groupes, pour le filtre de la liste des établissements. */
export function useFacilityGroupOptions(enabled: boolean = true) {
    // Les groupes changent rarement : inutile de les recharger à chaque visite.
    return useApiQuery<ApiResponse<Facility[]>>(
        enabled ? '/facilities-all?is_group=1&per_page=500&sort=name' : '',
        {},
        { enabled, staleTime: 30 * 60 * 1000 }
    );
}

/**
 * Hook to get all facilities with pagination
 * @param isGroup
 * @returns {<ApiResponse<Facility[]>, ApiError>}
 */
export function useAllGroupFacilities(isGroup: boolean = false) {
    const url = `/facilities-group-all?is_group=${isGroup ? 1 : 0}`;
    return useApiQuery<ApiResponse<Facility[]>>(url);
}

/**
 * Hook to get all facilities with GPS coordinates (for map view)
 * Super admin only
 * @returns FacilityWithCoordinates[]
 */
export function useFacilitiesWithCoordinates() {
    const url = '/facilities-with-coordinates';
    return useApiQuery<FacilityWithCoordinates[]>(url);
}

/**
 * Hook to get the groups of the facility
 * @returns {<ApiResponse<Facility[]>, ApiError>}
 */
export function useFacilityGroups() {
    const url = `/facility-groups-accepted`;
    return useApiQuery<ApiResponse<Facility[]>>(url);
}

/**
 * Hook to get a single facility
 * @param {number | string | null} id
 * @returns Facility
 */
export function useFacility(id: number | string | null) {
    const url = id ? `/facilities/${id}` : '';
    return useApiQuery<Facility>(url);
}

/**
 * Hook to get a facility with its child facilities (for groups)
 * @param {number | null} facilityId - The facility ID (should be a group)
 * @returns FacilityWithChildren
 */
export function useFacilityWithChildren(facilityId: number | null | undefined) {
    const url = facilityId ? `/facilities/${facilityId}/with-children` : '';
    return useApiQuery<FacilityWithChildren>(url, {}, {
        enabled: !!facilityId,
    });
}


/**
 * Hook to get a single facility with its favorite surveys
 * @returns Facility
 */
export function useFacilityFavoriteSurveys() {
    const url = `/facility-favorite-surveys`
    return useApiQuery<Facility>(url);
}

/**
 * Hook to delete a facilities
 * @returns Facility
 */
export function Facility() {
    const queryClient = useQueryClient();

    // Notez maintenant que nous passons une fonction pour générer l'URL basée sur l'ID
    return useApiMutation<Facility, ApiError, { id: number }>(
        'delete',
        ({ id }) => `/facilities/${id}`,
        {
            // Ajoutez la logique onSuccess ici si nécessaire
            onSuccess: () => {
                // Invalidez les requêtes de liste d'utilisateurs ici si nécessaire
                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });
            },
        }
    );
}

/**
 * Hook to update a facility
 * @param {MutationOptions<Facility, ApiError, Facility>} options
 */
export function useUpdateFacility(options: MutationOptions<Facility, ApiError, Facility> = {}) {

    const queryClient = useQueryClient();

    return useApiMutation<Facility, ApiError, Facility>(
        'put',
        (variables) => `/facilities/${variables.id}`,
        {
            ...options,
            onSuccess: (data, variables, context) => {

                // Invalide les requêtes nécessaires:
                queryClient.invalidateQueries(`/facilities/${variables.id}`);

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/users'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Assurez-vous de propager l'appel à l'onSuccess original, si fourni
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred while updating the facilities", error);
                // Propagation de l'appel à l'onError original, si fourni
                options.onError?.(error, variables, context);
            },
        }
    );
}


/**
 * Hook to update a facility
 * @param {MutationOptions<Facility, ApiError, Facility>} options
 */
export function useUpdateFavoriteSurveyFacility(options: MutationOptions<Facility, ApiError, Facility> = {}) {

    const queryClient = useQueryClient();

    return useApiMutation<Facility, ApiError, Facility>(
        'put',
        (variables) => `/facility-favorite-surveys/${variables.id}`,
        {
            ...options,
            onSuccess: (data, variables, context) => {

                // Invalide les requêtes nécessaires:
                queryClient.invalidateQueries(`/facilities/${variables.id}`);

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/users'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Assurez-vous de propager l'appel à l'onSuccess original, si fourni
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred while updating the facilities", error);
                // Propagation de l'appel à l'onError original, si fourni
                options.onError?.(error, variables, context);
            },
        }
    );
}

/**
 * Hook to create a facilities
 * @param {MutationOptions<Facility, ApiError, Facility>} options
 */
export function useCreateFacility(options: MutationOptions<Facility, ApiError, Facility> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Facility, ApiError, Facility>(
        'post',
        () => '/facilities',
        {
            ...options,
            onSuccess: (data, variables, context) => {
                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });
                queryClient.invalidateQueries(`/user`);
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred", error);
                options.onError?.(error, variables, context);
            },
        }
    );
}

/**
 * Hook to send a test email
 * @returns {<ApiResponse<Facility>, ApiError>}
 */
export function useSendTestEmail() {
    return useApiMutation(
        'post',
        (payload) => `/facilities/test-smtp/${payload.facility_id}`, // Utilisez l'id du facility
        {
            onSuccess: () => {
                // Email de test envoyé avec succès
            },
            onError: () => {
                // Erreur gérée par le composant appelant
            }
        }
    );
}


export interface FacilityThaisInfo {
    subdomain: string;
}

export function useFacilityThaisInfo(
    // On accepte les options de React-Query en 3ᵉ paramètre
    options?: UseQueryOptions<FacilityThaisInfo, ApiError>
) {
    const url = "/facility-thais-info";
    return useApiQuery<FacilityThaisInfo>(
        url,
        {},   // <-- 1) config Axios vide ou surchargé si besoin
        {
            // 2) ici vos options React-Query
            enabled: options?.enabled ?? true,
            ...options,
        }
    );
}




